/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, Theme, useTheme } from '@emotion/react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { EDateFormats, EHoursFormat } from '../lib/timeUtils';
import { AsideContainer } from './atoms/AsideContainer';
import { DndProvider } from './atoms/DndProvider';
import { HeaderContainer } from './atoms/HeaderContainer';
import { spaceWidth } from './atoms/styles';
import { MiddlewareContextProvider, useMiddlewareContext } from './hooks/useMiddlewareContext';
import { bodyContainer, bodyContent, cssProvider, dndProviderContainer, rootContainer } from './styles';
import { ECalendarMode, ECalendarView, ICalendarProps, IResource, IScrollTo, SCROLL_TO_DRAFT_SLOT } from './types';
import { findResource, getBodyContainerHeight, scrollToHorizontal, scrollToVertical } from './utils';
import { oneHourHeight, oneHourWidth } from './utils/timeUtils';
import { CalendarStoreProvider } from './CalendarStoreProvider';
import { CalendarView } from './CalendarView';
import { useCalendarStoreSelector } from './hooks/useCalendarStoreSelector';

dayjs.extend(customParseFormat);

const isSafari =
	typeof window !== 'undefined'
		? window.navigator.vendor &&
			window.navigator.vendor.indexOf('Apple') > -1 &&
			window.navigator.userAgent &&
			window.navigator.userAgent.indexOf('CriOS') == -1 &&
			window.navigator.userAgent.indexOf('FxiOS') == -1
		: false;

const SCROLL_BEHAVIOR = 'smooth';

const SCROLL_TO_BEHAVIOR = 'instant';

/** Id of the element that scrolls the calendar grid and its header. */
export const CALENDAR_SCROLL_CONTAINER_ID = 'calendarScrollContainer';

type SACalendarProps = Pick<
	ICalendarProps,
	'EventComponent' | 'DragOverlayComponent' | 'ResourceGroupHeader' | 'isLoading' | 'groups' | 'view'
>;

const Calendar: React.FC<SACalendarProps> = React.memo(
	({ EventComponent, DragOverlayComponent, ResourceGroupHeader, isLoading = false, groups, view }) => {
		const colors: Theme = useTheme();
		const { day: daysCount, getTopAndHeight, options = {}, dispatchChange } = useMiddlewareContext();

		const resources: IResource[] = useCalendarStoreSelector(state => state.resources);

		const [resourceGroups, setResourceGroups] = useState<Record<number, number[]> | null>(null);
		const [resourceId, setResourceId] = useState<number>(0);
		const [width, setWidth] = useState(200);

		const headerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
		const bodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
		const verticalSpacesRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

		const { mode = ECalendarMode.DAILY } = options;

		const horizontal: boolean = view === ECalendarView.HORIZONTAL;

		const bodyContainerHeight: number = getBodyContainerHeight(resources, mode, horizontal);

		useEffect(() => {
			setResourceGroups(
				Object.fromEntries(Object.keys(groups ?? {}).map(id => [id, groups[id]?.[0]?.childrenSlotIds]))
			);
		}, [groups]);

		useEffect(() => {
			if ((mode === ECalendarMode.MONTHLY || mode === ECalendarMode.DAYX) && resources?.length) {
				setResourceId((prevResourceId: number) => {
					const resource: IResource | null = findResource(resources, prevResourceId);

					if (!resource) {
						return resources?.[0]?.id ?? 0;
					}

					return prevResourceId;
				});
			}
		}, [mode, resources]);

		const selectResourceGroup = useCallback((id: number, childrenSlotsIds?: number[]) => {
			setResourceGroups(prev => {
				if (!prev) {
					return { [id]: childrenSlotsIds ?? [] };
				}

				return { ...prev, [id]: childrenSlotsIds ?? [] };
			});
		}, []);

		const scrollToHour = useCallback(
			(now: number) => {
				if (mode === ECalendarMode.DAILY && bodyRef.current) {
					if (isSafari) {
						if (horizontal) {
							scrollToHorizontal(bodyRef.current, now * oneHourWidth);
							scrollToVertical(bodyRef.current, 0);
						} else {
							scrollToHorizontal(bodyRef.current, 0);
							scrollToVertical(bodyRef.current, now * oneHourHeight);
						}
					} else {
						if (horizontal) {
							bodyRef.current.scrollTo({ left: now * oneHourWidth, top: 0, behavior: SCROLL_BEHAVIOR });
						} else {
							bodyRef.current.scrollTo({ left: 0, top: now * oneHourHeight, behavior: SCROLL_BEHAVIOR });
						}
					}
				}
			},
			[horizontal, mode]
		);

		const scrollTo = useCallback(
			(scrollTo: IScrollTo) => {
				const { resourceId, startTime, endTime } = scrollTo || {};
				const body = bodyRef?.current;

				if (!body) {
					return;
				}

				const resource = document.getElementById(`resource_${resourceId}`);

				let resourcePosition = 0;
				if (resource) {
					if (horizontal) {
						resourcePosition = resource.offsetTop;
					} else {
						resourcePosition = resource.offsetLeft + resource.clientWidth / 2;
					}
				}

				let hoursPosition = 0;
				if (startTime && endTime) {
					const date = dayjs(options?.date).format('DD/MM/YYYY');
					const parseFormat = 'DD/MM/YYYY HH:mm';

					const start = dayjs(`${date} ${startTime}`, parseFormat);
					let end = dayjs(`${date} ${endTime}`, parseFormat);
					if (dayjs(start).diff(end, 'millisecond') >= 0) {
						end = end.add(1, 'day');
					}

					const { top, height } = getTopAndHeight(start.toDate().getTime(), end.toDate().getTime(), horizontal);

					hoursPosition = top + height / 2;
					if (!horizontal) hoursPosition += height / 2;
				}

				const halfBodyWidth = body.clientWidth / 2;
				const halfHoursHeight = body.clientHeight / 2;

				if (isSafari) {
					if (horizontal) {
						if (hoursPosition) {
							scrollToHorizontal(body, hoursPosition - halfBodyWidth);
						}
						if (resourcePosition) {
							scrollToVertical(body, resourcePosition - halfHoursHeight);
						}
					} else {
						if (resourcePosition) {
							scrollToHorizontal(body, resourcePosition - halfBodyWidth);
						}
						if (hoursPosition) {
							scrollToVertical(body, hoursPosition - halfHoursHeight);
						}
					}
				} else {
					if (horizontal) {
						if (hoursPosition && resourcePosition) {
							body.scrollTo(hoursPosition - halfBodyWidth, resourcePosition - halfHoursHeight);
						} else if (hoursPosition) {
							body.scrollTo(hoursPosition - halfBodyWidth, body.scrollTop);
						} else if (resourcePosition) {
							body.scrollTo(body.scrollLeft, resourcePosition - halfHoursHeight);
						}
					} else {
						if (hoursPosition && resourcePosition) {
							body.scrollTo({
								left: resourcePosition - halfBodyWidth,
								top: hoursPosition - halfHoursHeight,
								behavior: SCROLL_BEHAVIOR,
							});
						} else if (resourcePosition) {
							body.scrollTo({ left: resourcePosition - halfBodyWidth, top: body.scrollTop, behavior: SCROLL_BEHAVIOR });
						} else if (hoursPosition) {
							body.scrollTo({ left: body.scrollLeft, top: hoursPosition - halfHoursHeight, behavior: SCROLL_BEHAVIOR });
						}
					}
				}
			},
			[getTopAndHeight, horizontal, options?.date, options?.scrollTo, isSafari]
		);

		const scrollToCurrentHourOnFirstRender = useCallback(() => {
			const currentHour: number = Number(dayjs().format(EHoursFormat.HH));
			scrollToHour(currentHour);
		}, [scrollToHour]);

		useEffect(() => {
			const currentDate: string = dayjs().format(EDateFormats.YYYY_MM_DD);
			if (options?.date === currentDate) {
				scrollToCurrentHourOnFirstRender();
			}
		}, [options?.date]);

		useEffect(() => {
			const currentDate: string = dayjs().format(EDateFormats.YYYY_MM_DD);
			scrollToHour(options?.date === currentDate ? Number(dayjs().format(EHoursFormat.HH)) : 0);
		}, [horizontal]);

		useEffect(() => {
			const onScrollHandler = (e: CustomEvent) => {
				scrollTo(e.detail);
			};

			window.addEventListener(SCROLL_TO_DRAFT_SLOT, onScrollHandler as EventListener);

			return () => {
				window.removeEventListener(SCROLL_TO_DRAFT_SLOT, onScrollHandler as EventListener);
			};
		}, [scrollTo]);

		useEffect(() => {
			let resizeObserver: ResizeObserver;

			if (bodyRef?.current) {
				resizeObserver = new ResizeObserver(data => {
					setWidth(data?.[0]?.contentRect?.width || 0);
				});
				resizeObserver.observe(bodyRef?.current);
			}

			return () => {
				resizeObserver.disconnect();
			};
		}, [view, options?.hourSize, options?.resourceSize, bodyRef?.current, isLoading]);

		useEffect(() => {
			const debounce = (func: (...args: any[]) => void, wait: number) => {
				let timeout: NodeJS.Timeout;
				return (...args: any[]) => {
					clearTimeout(timeout);
					timeout = setTimeout(() => func(...args), wait);
				};
			};

			let mirroredToHeader = -1;
			let mirroredToBody = -1;

			const handleScroll = (e: Event) => {
				const body = e.target as Element;

				if (
					headerRef.current &&
					body.scrollLeft !== mirroredToBody &&
					headerRef.current.scrollLeft !== body.scrollLeft
				) {
					mirroredToHeader = body.scrollLeft;
					headerRef.current.scrollLeft = body.scrollLeft;
				}
				if (verticalSpacesRef.current && verticalSpacesRef.current.scrollTop !== body.scrollTop) {
					verticalSpacesRef.current.scrollTop = body.scrollTop;
				}
				debouncedOnChange({
					type: 'SCROLL',
					data: {
						scrollTop: body.scrollTop ?? 0,
						scrollLeft: body.scrollLeft ?? 0,
					},
				});
			};

			// The header lives inside the scroller, so its scroll events reach the scroller in the capture phase
			const handleHeaderScroll = (e: Event) => {
				const header = e.target as Element;

				if (header !== headerRef.current || header.scrollLeft === mirroredToHeader) {
					return;
				}

				if (bodyRef.current && bodyRef.current.scrollLeft !== header.scrollLeft) {
					mirroredToBody = header.scrollLeft;
					bodyRef.current.scrollTo({
						left: header.scrollLeft,
						top: bodyRef.current.scrollTop,
						behavior: SCROLL_TO_BEHAVIOR as ScrollBehavior,
					});
				}
			};

			const debouncedOnChange = debounce(dispatchChange!, 400);
			const body: HTMLDivElement | null = bodyRef.current;

			body?.addEventListener('scroll', handleScroll, false);
			body?.addEventListener('scroll', handleHeaderScroll, true);

			return () => {
				body?.removeEventListener('scroll', handleScroll, false);
				body?.removeEventListener('scroll', handleHeaderScroll, true);
			};
		}, [mode, view]);

		return (
			<div
				css={cssProvider(colors, spaceWidth, mode === ECalendarMode.MONTHLY, isLoading)}
				data-aid="SACalendar-calendar"
			>
				<div css={rootContainer}>
					<AsideContainer
						bodyContainerHeight={bodyContainerHeight}
						verticalSpacesRef={verticalSpacesRef}
						width={width}
						ResourceGroupHeader={ResourceGroupHeader}
						groups={groups}
						selectedMonthlyResource={resourceId}
						mode={mode}
						view={view}
						resourceGroups={resourceGroups}
						selectResourceGroup={selectResourceGroup}
					/>
					<DndProvider
						DragOverlayComponent={DragOverlayComponent}
						onChange={dispatchChange}
						horizontal={horizontal}
						view={view}
					>
						<div
							key="calendar-dnd-provider-container"
							id={CALENDAR_SCROLL_CONTAINER_ID}
							css={dndProviderContainer(colors)}
							ref={bodyRef}
						>
							<HeaderContainer
								headerRef={headerRef}
								width={width}
								ResourceGroupHeader={ResourceGroupHeader}
								resourceId={resourceId}
								groups={groups}
								mode={mode}
								view={view}
								selectResourceGroup={selectResourceGroup}
								resourceGroups={![ECalendarMode.DAYX, ECalendarMode.MONTHLY].includes(mode) ? resourceGroups : null}
								handleSetSelectedMonthlyResource={setResourceId}
							/>
							<div key="calendar-contaimer" id="bodyContainer" css={bodyContainer(horizontal, width)}>
								<div
									key="calendar-content"
									css={bodyContent(bodyContainerHeight * (!horizontal ? daysCount : 1), horizontal)}
								>
									<CalendarView
										EventComponent={EventComponent}
										isLoading={isLoading}
										resourceGroups={resourceGroups}
										resourceId={resourceId}
										view={view}
									/>
								</div>
							</div>
						</div>
					</DndProvider>
				</div>
			</div>
		);
	}
);

Calendar.displayName = 'Calendar';

export const SACalendar = React.memo(
	({
		EventComponent,
		DragOverlayComponent,
		ResourceGroupHeader,
		onChange,
		isLoading,
		groups,
		view,
		slots,
		resources,
		options,
	}: ICalendarProps) => {
		return (
			<CalendarStoreProvider slots={slots} resources={resources}>
				<MiddlewareContextProvider onChange={onChange} options={options}>
					<Calendar
						EventComponent={EventComponent}
						DragOverlayComponent={DragOverlayComponent}
						ResourceGroupHeader={ResourceGroupHeader}
						isLoading={isLoading}
						groups={groups}
						view={view}
					/>
				</MiddlewareContextProvider>
			</CalendarStoreProvider>
		);
	}
);

SACalendar.displayName = 'SACalendar';
